import { SOCIAL_OG_IMAGE, SOCIAL_OG_IMAGE_ALT } from "@/lib/social-image";
import { BlogHero } from "@/components/BlogHero";
import { blogHeroUrl, blogHeroAlt } from "@/data/_media/blog-hero";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ctrDesc, ctrTitle } from "@/lib/seo-copy";
import { SITE } from "@/lib/site-config";
import {
  buildJsonLd,
  jsonLdScript,
  webPageSchema,
  breadcrumbListSchema,
  articleSchema,
  faqPageSchema,
} from "@/lib/schema";
import { canonicalAndHreflang } from "@/lib/hreflang";

/** Japanese mirror of the apostille vs legalisation guide. */
const TITLE = "アポスティーユと領事認証の違い — タイ文書の国別ガイド";
const DESC =
  "タイの書類を海外で使うための認証方法：アポスティーユと領事認証の違い、正しい手順、主要国ごとの注意点を整理します。";
const PATH = "/blog/apostille-vs-legalization-by-country";
const URL = `/ja${PATH}`;
const PUBLISHED = "2026-08-10";

const FAQ = [
  {
    question: "アポスティーユとは何ですか？",
    answer:
      "ハーグ条約に基づく一枚の証明書で、加盟国間では領事認証の連鎖を代替します。提出先が加盟国であり、その機関が受理する場合に限り、大使館・領事館での認証を省けます。",
  },
  {
    question: "領事認証はどのような流れですか？",
    answer:
      "原本または認証済み謄本の取得、私文書の場合は公証人（Notary）による署名認証、外務省領事局による認証、翻訳、最後に提出先国の大使館・領事館での認証という順に進みます。",
  },
  {
    question: "翻訳と認証はどちらが先ですか？",
    answer:
      "原則として認証を受けた後に翻訳します。先に翻訳すると、後から付された認証印やスタンプが訳文に反映されず、差し戻しの原因になります。",
  },
  {
    question: "コピーでも認証できますか？",
    answer:
      "多くの場合は不可です。原本、または発行機関が交付した認証済み謄本が必要です。単純なコピーは受理されないことが一般的です。",
  },
  {
    question: "書類の有効期間はありますか？",
    answer:
      "戸籍関係や無犯罪証明などは発行から1〜6か月以内を求める機関が少なくありません。各段階に営業日を要するため、提出期限から逆算して計画してください。",
  },
  {
    question: "委任状で代理申請できますか？",
    answer:
      "可能ですが、申請・受領・認証手続き・提出のすべてを委任範囲に含める必要があります。範囲が狭いと途中で手続きが止まります。",
  },
  {
    question: "差し戻しの主な原因は何ですか？",
    answer:
      "手順の逆転、印章やスタンプ文言の訳し漏れ、氏名スペルの不一致、有効期間切れ、コピーの提出などです。",
  },
];

export const Route = createFileRoute("/ja/blog/apostille-vs-legalization-by-country")({
  component: JaApostilleVsLegalisation,
  loader: () => ({
    h: {
      meta: [
        { title: ctrTitle(`${TITLE} — ${SITE.name}`) },
        { name: "description", content: ctrDesc(DESC) },
        { property: "og:title", content: ctrTitle(TITLE) },
        { property: "og:description", content: ctrDesc(DESC) },
        { property: "og:type", content: "article" },
        { property: "og:url", content: `https://${SITE.canonicalHost}${URL}` },
        { property: "og:locale", content: "ja" },
        { name: "twitter:card", content: "summary_large_image" },
        { property: "og:image", content: blogHeroUrl("apostille-vs-legalization-by-country") ?? SOCIAL_OG_IMAGE },
        { property: "og:image:alt", content: blogHeroAlt("apostille-vs-legalization-by-country", "ja") ?? SOCIAL_OG_IMAGE_ALT },
        { name: "twitter:image", content: blogHeroUrl("apostille-vs-legalization-by-country") ?? SOCIAL_OG_IMAGE },
        {
          name: "keywords",
          content:
            "アポスティーユ タイ, タイ 領事認証, タイ 書類 認証 手順, タイ外務省 領事局 認証, タイ 公証人 認証",
        },
      ],
      links: canonicalAndHreflang(PATH, null, "ja"),
      scripts: [
        jsonLdScript(
          buildJsonLd([
            webPageSchema({ url: URL, title: TITLE, description: DESC, lastModified: PUBLISHED, speakable: true }),
            breadcrumbListSchema([
              { name: "ホーム", url: "/ja" },
              { name: "ガイド", url: "/blog" },
              { name: TITLE, url: URL },
            ]),
            articleSchema({
            image: blogHeroUrl("apostille-vs-legalization-by-country"),
              url: URL,
              headline: TITLE,
              description: DESC,
              datePublished: PUBLISHED,
              dateModified: PUBLISHED,
              articleSection: "Apostille & Legalisation",
            }),
            faqPageSchema(FAQ),
          ]),
        ),
      ],
    },
  }),
  head: ({ loaderData }) => loaderData?.h ?? {},
});

const SEQUENCE: string[] = [
  "提出先機関に必要な認証の層・受理言語・有効期間を確認する",
  "原本または発行機関の認証済み謄本を取得する",
  "私文書は公証人による署名認証を受ける",
  "必要に応じて外務省領事局の認証を受ける",
  "印章・スタンプ・注記まで含めて全文を翻訳する",
  "提出先国の大使館・領事館で領事認証、または可能ならアポスティーユ経路に切り替える",
];

const COUNTRIES: Array<{ c: string; note: string }> = [
  { c: "オーストラリア", note: "多くの機関がNAATI認定翻訳者を要求。書類により領事局認証も必要になります。" },
  { c: "アメリカ", note: "提出先の州・機関により異なり、公証人認証に加えて追加の認証を求められることがあります。" },
  { c: "中国本土", note: "完全な認証連鎖を求められるのが一般的で、訳文は原本と完全に対応している必要があります。" },
  { c: "日本・韓国", note: "領事局認証の後に大使館・領事館での認証が一般的。氏名表記はパスポートと一致させます。" },
  { c: "欧州各国", note: "提出先が条約加盟国でアポスティーユを受理する場合、大使館認証を省ける可能性があります。" },
  { c: "中東各国", note: "完全な領事認証連鎖に加え、翻訳言語の指定があることが多いです。" },
];

const MISTAKES: string[] = [
  "認証より先に翻訳し、認証内容が訳文に含まれていない",
  "原本や認証済み謄本ではなく単純コピーを使用した",
  "同一ファイル内で氏名のスペルが一致していない",
  "発行が早すぎて提出時に有効期間を過ぎている",
  "委任状の権限が狭く、後続の認証・提出を含んでいない",
  "印章・スタンプ・注記の文言を訳し漏れている",
];

function JaApostilleVsLegalisation() {
  return (
    <article className="mx-auto max-w-3xl px-4 py-12 prose prose-slate dark:prose-invert">
      <nav className="text-sm mb-6 not-prose">
        <Link to="/ja" className="text-muted-foreground hover:text-foreground">
          ホーム
        </Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <Link to="/blog" className="text-muted-foreground hover:text-foreground">
          ガイド
        </Link>
      </nav>

      <h1>{TITLE}</h1>
      <BlogHero slug="apostille-vs-legalization-by-country" locale="ja" />
      <p className="answer-summary speakable-summary">
        タイの書類が差し戻される原因は内容の誤りよりも、認証の順序違い、印章文言の訳し漏れ、有効期間切れです。
        提出期限から逆算し、提出先の要件を確認したうえで「原本 → 公証人 → 領事局 → 翻訳 → 大使館」の順に進めるのが確実です。
      </p>

      <h2>1. 二つの経路の違い</h2>
      <p>
        アポスティーユは一枚の証明書で認証連鎖を代替し、条約加盟国間で相互に認められます。
        領事認証は各段階を順に踏み、最後に提出先国の在外公館が認証します。判断基準は「提出先機関が現在どちらを受理するか」の一点です。最新の案内で必ず確認してください。
      </p>

      <h2>2. 正しい手順</h2>
      <ol>
        {SEQUENCE.map((s) => (
          <li key={s}>{s}</li>
        ))}
      </ol>

      <h2>3. 国別の注意点</h2>
      <ul>
        {COUNTRIES.map((x) => (
          <li key={x.c}>
            <strong>{x.c}：</strong> {x.note}
          </li>
        ))}
      </ul>

      <h2>4. よくある差し戻し理由</h2>
      <ul>
        {MISTAKES.map((m) => (
          <li key={m}>{m}</li>
        ))}
      </ul>

      <h2>5. よくある質問</h2>
      <dl>
        {FAQ.map((f) => (
          <div key={f.question}>
            <dt>
              <strong>{f.question}</strong>
            </dt>
            <dd>{f.answer}</dd>
          </div>
        ))}
      </dl>

      <h2>6. 参考リンクと関連サービス</h2>
      <p className="text-sm">
        参考：{" "}
        <a href="https://consular.mfa.go.th/en/publicservice/legalization" target="_blank" rel="noreferrer">
          タイ外務省 領事局
        </a>{" "}
        ·{" "}
        <a href="https://www.hcch.net/en/instruments/conventions/specialised-sections/apostille" target="_blank" rel="noreferrer">
          HCCH ハーグ認証条約
        </a>
      </p>
      <ul className="not-prose mt-4 grid gap-2 text-sm">
        <li>
          <Link to="/ja/lgz" className="text-primary underline">
            文書認証・領事認証サービス
          </Link>
        </li>
        <li>
          <Link to="/ja/notary" className="text-primary underline">
            公証人（Notary）サービス
          </Link>
        </li>
        <li>
          <Link to="/ja/naati" className="text-primary underline">
            NAATI認定翻訳
          </Link>
        </li>
      </ul>
      <p className="text-sm text-muted-foreground">
        内容確認日 {PUBLISHED}。本ガイドは計画のための一般情報であり、いかなる機関の決定を示すものではありません。手続き前に発行機関および提出先機関で最新要件をご確認ください。
      </p>
    </article>
  );
}
